import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider, useAuth } from '@/lib/auth';
import Landing from '@/pages/Landing';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import Feed from '@/pages/Feed';
import Bubbles from '@/pages/Bubbles';
import BubbleDetail from '@/pages/BubbleDetail';
import PassBubble from '@/pages/PassBubble';
import ClaimPass from '@/pages/ClaimPass';
import Tribes from '@/pages/Tribes';
import TribeDetail from '@/pages/TribeDetail';
import Leaderboards from '@/pages/Leaderboards';
import Profile from '@/pages/Profile';
import Bursts from '@/pages/Bursts';
import Charity from '@/pages/Charity';
import Admin from '@/pages/Admin';
import Ledger from '@/pages/Ledger';
import Onboarding from '@/pages/Onboarding';
import Drops from '@/pages/Drops';
import DropSuccess from '@/pages/DropSuccess';
import Trinkets from '@/pages/Trinkets';
import TrinketSuccess from '@/pages/TrinketSuccess';
import Curator from '@/pages/Curator';
import Sponsor from '@/pages/Sponsor';
import SponsorSuccess from '@/pages/SponsorSuccess';
import Memorial from '@/pages/Memorial';
import HallOfFame from '@/pages/HallOfFame';
import Celebrities from '@/pages/Celebrities';
import Invites from '@/pages/Invites';
import PublicBubble from '@/pages/PublicBubble';
import VisionFeed from '@/pages/VisionFeed';
import StarsWall from '@/pages/StarsWall';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import Breathe from '@/pages/Breathe';
import ResonanceHub from '@/pages/ResonanceHub';
import CreatorProfile from '@/pages/CreatorProfile';
import Beacons from '@/pages/Beacons';
import BeaconScan from '@/pages/BeaconScan';
import Brush from '@/pages/Brush';
import BrushFinish from '@/pages/BrushFinish';
import Genesis from '@/pages/Genesis';
import Encounters from '@/pages/Encounters';
import CargoHub from '@/pages/CargoHub';
import Swarms from '@/pages/Swarms';
import SwarmDetail from '@/pages/SwarmDetail';
import Marketplace from '@/pages/Marketplace';
import TourBubbles from '@/pages/TourBubbles';
import Compass from '@/pages/Compass';
import Discover from '@/pages/Discover';
import Clubs from '@/pages/Clubs';
import Vault from '@/pages/Vault';
import Shop from '@/pages/Shop';
import Lighthouses from '@/pages/Lighthouses';
import LighthouseDetail from '@/pages/LighthouseDetail';
import StrangerPool from '@/pages/StrangerPool';
import WorldEventDetail from '@/pages/WorldEventDetail';
import ExpressGenesis from '@/pages/ExpressGenesis';
import BubbleHome from '@/pages/BubbleHome';
import { About, Contact, Terms, Privacy, Cookies } from '@/pages/Legal';
import AppShell from '@/components/AppShell';
import '@/App.css';

const Private = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center text-white/60">loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

const AdminOnly = ({ children }) => {
  const { user } = useAuth();
  if (!user?.is_admin) return <Navigate to="/home" replace />;
  return children;
};

function App() {
  const GCID = process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID;
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT
  // URLS, THIS BREAKS THE AUTH
  return (
    <GoogleOAuthProvider clientId={GCID}>
    <AuthProvider>
      <BrowserRouter>
        <Toaster theme="dark" position="top-center" richColors closeButton />
        <Suspense fallback={<div className="min-h-screen grid place-items-center text-white/60">loading…</div>}>
          <Routes>
            <Route path="/" element={<Genesis />} />
            <Route path="/manifesto" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/claim/:token" element={<ClaimPass />} />
            <Route path="/b/:id" element={<PublicBubble />} />
            <Route path="/hall-of-fame" element={<HallOfFame />} />
            <Route path="/memorial/:id" element={<Memorial />} />
            <Route path="/celebrities" element={<Celebrities />} />
            <Route path="/vision" element={<VisionFeed />} />
            <Route path="/stars" element={<StarsWall />} />
            <Route path="/creator/:username" element={<CreatorProfile />} />
            <Route path="/beacon/:code" element={<BeaconScan />} />
            <Route path="/brush/finish/:token" element={<BrushFinish />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/cookies" element={<Cookies />} />

            <Route element={<Private><AppShell /></Private>}>
              <Route path="/onboarding" element={<Onboarding />} />
              <Route path="/home" element={<BubbleHome />} />
              <Route path="/feed" element={<Feed />} />
              <Route path="/encounters" element={<Encounters />} />
              <Route path="/cargo" element={<CargoHub />} />
              <Route path="/swarms" element={<Swarms />} />
              <Route path="/swarm/:id" element={<SwarmDetail />} />
              <Route path="/market" element={<Marketplace />} />
              <Route path="/tour-bubbles" element={<TourBubbles />} />
              <Route path="/compass" element={<Compass />} />
              <Route path="/discover" element={<Discover />} />
              <Route path="/clubs" element={<Clubs />} />
              <Route path="/vault" element={<Vault />} />
              <Route path="/shop" element={<Shop />} />
              <Route path="/adorn" element={<Shop />} />
              <Route path="/lighthouses" element={<Lighthouses />} />
              <Route path="/lighthouse/:id" element={<LighthouseDetail />} />
              <Route path="/candle" element={<StrangerPool />} />
              <Route path="/stranger-pool" element={<StrangerPool />} />
              <Route path="/world/:id" element={<WorldEventDetail />} />
              <Route path="/bubbles" element={<Bubbles />} />
              <Route path="/bubble/:id" element={<BubbleDetail />} />
              <Route path="/pass/:id" element={<PassBubble />} />
              <Route path="/tribes" element={<Tribes />} />
              <Route path="/tribe/:id" element={<TribeDetail />} />
              <Route path="/leaderboards" element={<Leaderboards />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/bursts" element={<Bursts />} />
              <Route path="/charity" element={<Charity />} />
              <Route path="/ledger" element={<Ledger />} />
              <Route path="/drops" element={<Drops />} />
              <Route path="/drops/success" element={<DropSuccess />} />
              <Route path="/trinkets" element={<Trinkets />} />
              <Route path="/trinkets/success" element={<TrinketSuccess />} />
              <Route path="/admin" element={<AdminOnly><Admin /></AdminOnly>} />
              <Route path="/admin/curator" element={<AdminOnly><Curator /></AdminOnly>} />
              <Route path="/sponsor" element={<Sponsor />} />
              <Route path="/sponsor/success" element={<SponsorSuccess />} />
              <Route path="/invites" element={<Invites />} />
              <Route path="/hatch" element={<Breathe />} />
              <Route path="/breathe" element={<Breathe />} />
              <Route path="/resonance" element={<ResonanceHub />} />
              <Route path="/beacons" element={<Beacons />} />
              <Route path="/brush" element={<Brush />} />
              <Route path="/brush/:bubbleId" element={<Brush />} />
              <Route path="/express" element={<ExpressGenesis />} />
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
